import { useCallback, useRef, useState } from 'react'
import {
	ConsoleEffect,
	ConsoleEvent,
	ConsoleState,
	INITIAL_CONSOLE,
	transition,
} from './model/presentation-machine'

export type SendConsoleEvent = (event: ConsoleEvent) => void

/**
 * The console's state machine in React: `send` moves it and runs the effects
 * of the move right away, in the same call stack - so a click's effects run
 * inside that click. The state is read from a ref, not from the last render,
 * so events sent by those effects build on the move before them.
 */
export const usePresentationMachine = (
	run: (effect: ConsoleEffect, send: SendConsoleEvent) => void
): [ConsoleState, SendConsoleEvent] => {
	const [state, setState] = useState<ConsoleState>(INITIAL_CONSOLE)
	const current = useRef(state)
	const runEffect = useRef(run)
	runEffect.current = run

	const send = useCallback<SendConsoleEvent>(event => {
		const [next, effects] = transition(current.current, event)
		current.current = next
		setState(next)
		effects.forEach(effect => runEffect.current(effect, send))
	}, [])

	return [state, send]
}
